import { ArticleReferenceModel } from '@app/models/article-reference.model';
import { FieldReferenceModel, IncidentModel } from '@app/models/incident.model';
import { MediaReferenceMetadataModel } from '@app/models/media-reference-metadata.model';
import { RimsPhaseInfo, RimsStepProperty, RimsStepPropertySchema, RimsStepSchema } from '@app/models/rims.model';
import { formatAsDateOnlyString } from '@app/utils/datetime/datetime.utils';
import { capitalizeRimsPath } from '@app/utils/rims/rims.utils';
import {
  FIELD_REFERENCE_NAMES_BY_ROW_FIELD,
  IncidentModelFieldName,
  MergeRow,
  MergeViewRows,
  ReferenceRow,
  RimsPhaseRow,
  RimsStepArrayItemColumn,
  RimsStepRow,
  RimsStepValueContainer,
  RowType,
  SingleColumnMergeRow,
} from './models/merge-row.model';
import { IncidentColumn, MergeColumn, MergeColumnValueType } from './models/merge-column.model';
import {
  detailsRowDefinitions,
  entityRowDefinitions,
  RowDefinition,
  threatActorRowDefinitions,
} from './merge-view-row-definitions';

const STEP_NOT_INCLUDED_LABEL = 'Step not included';

export function buildMergeViewRows(
  mergedIncident: IncidentModel,
  originalIncident: IncidentModel,
  duplicateIncidents: IncidentModel[],
  rimsPhaseInfos: RimsPhaseInfo[],
): MergeViewRows {
  const incidentColumns: IncidentColumn[] = getMergeViewColumns(mergedIncident, originalIncident, duplicateIncidents);

  const topHeaderColumns = incidentColumns.map((column) => ({
    incidentId: column.incident.id,
    label: column.label,
  }));

  // Create the merge rows
  const detailsRows = createDetailsRows(incidentColumns);
  const entityRows = createEntityRows(incidentColumns);
  const threatActorRows = createThreatActorRows(incidentColumns);
  const articleReferenceRows = createArticleReferenceRows(incidentColumns);
  const mediaReferenceRows = createMediaReferenceRows(incidentColumns);

  // Mark the selected cells using the field references.
  const sourcedReferencesByName = getSourcedReferencesByName(mergedIncident.fieldReferences);
  if (sourcedReferencesByName.size > 0) {
    const columnIndexByIncidentId = getSelectableColumnIndexByIncidentId(incidentColumns);

    applySourcedFieldReferenceSelections(
      [detailsRows, articleReferenceRows, mediaReferenceRows],
      sourcedReferencesByName,
      columnIndexByIncidentId,
    );

    applySourcedSelectionToRowCollection(
      entityRows,
      getSourcedColumnIndexByReferenceName('EntityId', sourcedReferencesByName, columnIndexByIncidentId),
    );
    applySourcedSelectionToRowCollection(
      threatActorRows,
      getSourcedColumnIndexByReferenceName('ActorId', sourcedReferencesByName, columnIndexByIncidentId),
    );
  }

  return {
    topHeaderColumns,
    detailsRows,
    entityRows,
    threatActorRows,
    articleReferenceRows,
    mediaReferenceRows,
    rimsPhaseRows: buildRimsPhaseRows(mergedIncident, originalIncident, duplicateIncidents, rimsPhaseInfos),
  };
}

// Restores which cell was selected for each row from the merged incident's sourced field references.
function applySourcedFieldReferenceSelections(
  rowGroups: (MergeRow | ReferenceRow)[][],
  sourcedReferencesByName: Map<string, FieldReferenceModel>,
  columnIndexByIncidentId: Map<number, number>,
): void {
  for (const rows of rowGroups) {
    for (const row of rows) {
      if (isSingleColumnMergeRow(row)) {
        applySourcedSelectionToReferenceRow(row, sourcedReferencesByName);
      } else {
        applySourcedSelectionToMergeRow(row, sourcedReferencesByName, columnIndexByIncidentId);
      }
    }
  }
}

function applySourcedSelectionToMergeRow(
  row: MergeRow,
  sourcedReferencesByName: Map<string, FieldReferenceModel>,
  columnIndexByIncidentId: Map<number, number>,
): void {
  const fieldReferenceNames = row.field ? FIELD_REFERENCE_NAMES_BY_ROW_FIELD[row.field] : undefined;
  if (!fieldReferenceNames) {
    return;
  }
  for (const fieldReferenceName of fieldReferenceNames) {
    const fieldReference = sourcedReferencesByName.get(fieldReferenceName);
    const columnIndex = fieldReference ? columnIndexByIncidentId.get(fieldReference.sourceId) : undefined;

    if (columnIndex === undefined) {
      continue;
    }

    for (const column of row.columns) {
      column.isChecked = false;
    }

    if (!row.columns[columnIndex]) {
      continue;
    }

    row.columns[columnIndex].isChecked = true;
    return;
  }
}

function getSourcedReferencesByName(
  fieldReferences: FieldReferenceModel[] | null | undefined,
): Map<string, FieldReferenceModel> {
  const sourcedReferencesByName = new Map<string, FieldReferenceModel>();

  for (const fieldReference of fieldReferences ?? []) {
    if (fieldReference.state === 'Sourced') {
      sourcedReferencesByName.set(fieldReference.name, fieldReference);
    }
  }

  return sourcedReferencesByName;
}

function getSelectableColumnIndexByIncidentId(incidentColumns: IncidentColumn[]): Map<number, number> {
  // The original incident shares the merged incident's id, so the merged column (index 0) is never a selectable source.
  const columnIndexByIncidentId = new Map<number, number>();

  for (let columnIndex = 1; columnIndex < incidentColumns.length; columnIndex++) {
    const incidentId = incidentColumns[columnIndex].incident.id;
    if (!columnIndexByIncidentId.has(incidentId)) {
      // <incidentId, columnIndex>>
      columnIndexByIncidentId.set(incidentId, columnIndex);
    }
  }

  return columnIndexByIncidentId;
}

function getSourcedColumnIndexByReferenceName(
  referenceName: string,
  sourcedReferencesByName: Map<string, FieldReferenceModel>,
  columnIndexByIncidentId: Map<number, number>,
): number | undefined {
  const fieldReference = sourcedReferencesByName.get(referenceName);
  if (!fieldReference) {
    return undefined;
  }

  return columnIndexByIncidentId.get(fieldReference.sourceId);
}

function applySourcedSelectionToRowCollection(rows: MergeRow[], columnIndex: number | undefined): void {
  if (columnIndex === undefined) {
    return;
  }

  for (const row of rows) {
    if (!row.columns[columnIndex]) {
      continue;
    }

    for (const column of row.columns) {
      column.isChecked = false;
    }

    row.columns[columnIndex].isChecked = true;
  }
}

function applySourcedSelectionToReferenceRow(
  row: SingleColumnMergeRow,
  sourcedReferencesByName: Map<string, FieldReferenceModel>,
): void {
  if (row.columnIndex <= 0) {
    return;
  }

  const referenceId =
    row.field === 'custom.articleReference'
      ? getGroupableArticleReferenceId(row.column.value ?? null)
      : getGroupableMediaReferenceId(row.column.value ?? null);

  if (referenceId === null) {
    return;
  }

  const fieldReferenceName = row.field === 'custom.articleReference' ? 'ArticleReferences' : 'MediaReferences';
  const fieldReference = sourcedReferencesByName.get(`${fieldReferenceName}/${referenceId}`);

  if (!fieldReference || fieldReference.state !== 'Sourced') {
    return;
  }

  row.column.isChecked = true;
}

function isSingleColumnMergeRow(row: MergeRow | ReferenceRow): row is SingleColumnMergeRow {
  return 'column' in row;
}

function buildRimsPhaseRows(
  mergedIncident: IncidentModel,
  originalIncident: IncidentModel,
  duplicateIncidents: IncidentModel[],
  rimsPhaseInfos: RimsPhaseInfo[],
): RimsPhaseRow[] {
  if (rimsPhaseInfos.length === 0) {
    return [];
  }

  const incidents = [mergedIncident, originalIncident, ...duplicateIncidents];
  const availableStepIds = new Set<string>();

  // Collect the union of step ids present in all displayed incidents (Merged + Original + Duplicates).
  // Example: if Merged has S-1001 and Original has S-1005, both steps are considered visible.
  for (const incident of incidents) {
    for (const technicalStep of incident.technicalSteps ?? []) {
      const stepId = technicalStep.rimsStep?.$type;
      if (stepId) {
        availableStepIds.add(stepId);
      }
    }
  }

  if (availableStepIds.size === 0) {
    return [];
  }

  return (
    rimsPhaseInfos
      .map((phaseInfo) => {
        // The steps are ordered by their stepId in ascending order.
        const steps = phaseInfo.rimsStepInfos
          .filter((stepInfo) => availableStepIds.has(stepInfo.stepId))
          .map((stepInfo) => ({
            stepId: stepInfo.stepId,
            stepName: stepInfo.stepName,
          }))
          .sort((left, right) => left.stepId.localeCompare(right.stepId, undefined, { numeric: true }));

        return {
          phaseId: phaseInfo.id,
          phaseName: phaseInfo.name,
          steps,
        };
      })
      // Do not render empty phases in the merge view.
      .filter((phase) => phase.steps.length > 0)
  );
}

export function buildRimsStepRows(
  stepId: string,
  stepSchema: RimsStepSchema,
  mergedIncident: IncidentModel,
  originalIncident: IncidentModel,
  duplicateIncidents: IncidentModel[],
): RimsStepRow[] {
  const incidentColumns: IncidentColumn[] = getMergeViewColumns(mergedIncident, originalIncident, duplicateIncidents);

  const stepDataByIncident = incidentColumns.map(({ incident }) => {
    const technicalStep = incident.technicalSteps?.find((step) => step.rimsStep?.$type === stepId);
    return {
      isIncluded: technicalStep !== undefined,
      value: technicalStep?.rimsStep ?? null,
    };
  });

  const stepValues = stepDataByIncident.map((stepData) => stepData.value);
  const isStepIncludedByIncident = stepDataByIncident.map((stepData) => stepData.isIncluded);

  const rows = buildRimsStepRowsRecursive(
    stepId,
    stepSchema.properties,
    stepValues,
    incidentColumns,
    isStepIncludedByIncident,
    '',
    0,
    false,
  );

  // Restore which cell/set item was selected for this step from the merged incident's sourced field references.
  const sourcedReferencesByName = getSourcedReferencesByName(mergedIncident.fieldReferences);

  if (sourcedReferencesByName.size > 0) {
    applySourcedRimsSelections(rows, stepId, sourcedReferencesByName, incidentColumns);
  }

  return rows;
}

// Recursively marks the selected cell (plain fields) or set item (array-item rows) for a RIMS step's rows.
function applySourcedRimsSelections(
  rows: RimsStepRow[],
  stepId: string,
  sourcedReferencesByName: Map<string, FieldReferenceModel>,
  incidentColumns: IncidentColumn[],
): void {
  for (const row of rows) {
    if (row.kind === 'field') {
      // Fields nested in a set item never show their own checkbox; selection is handled at the set-item level instead.
      if (!row.isSetItemField) {
        applySourcedSelectionToRimsFieldRow(row.row, sourcedReferencesByName, incidentColumns);
      }
      continue;
    }

    if (row.kind === 'array-item') {
      if (row.itemId != null) {
        // row.itemId is a guid and must never be capitalized.
        const columnIndex = getSourcedColumnIndexByTechnicalStepReferenceName(
          `${capitalizeRimsPath(stepId)}/Set/${row.itemId}`,
          sourcedReferencesByName,
          incidentColumns,
        );
        applySourcedSelectionToRimsSetItemRow(row.columns, columnIndex);

        if (columnIndex !== undefined) {
          // The whole set item is sourced from one incident, so every field nested inside it is checked too.
          applySelectionToRimsSetItemChildren(row.children, columnIndex);
        }
      }
    }

    applySourcedRimsSelections(row.children, stepId, sourcedReferencesByName, incidentColumns);
  }
}

function applySourcedSelectionToRimsFieldRow(
  row: MergeRow,
  sourcedReferencesByName: Map<string, FieldReferenceModel>,
  incidentColumns: IncidentColumn[],
): void {
  if (!row.field) {
    return;
  }

  // RIMS field row names are always `rims.${stepId}/${propertyPath}` in camelCase; backend field reference names are PascalCase.
  const referenceName = capitalizeRimsPath(row.field.slice('rims.'.length));
  const columnIndex = getSourcedColumnIndexByTechnicalStepReferenceName(
    referenceName,
    sourcedReferencesByName,
    incidentColumns,
  );

  if (columnIndex === undefined || !row.columns[columnIndex]) {
    return;
  }

  for (const column of row.columns) {
    column.isChecked = false;
  }

  row.columns[columnIndex].isChecked = true;
}

function getSourcedColumnIndexByTechnicalStepReferenceName(
  referenceName: string,
  sourcedReferencesByName: Map<string, FieldReferenceModel>,
  incidentColumns: IncidentColumn[],
): number | undefined {
  const fieldReference = sourcedReferencesByName.get(referenceName);
  if (!fieldReference) {
    return undefined;
  }

  return getColumnIndexByTechnicalStepId(fieldReference.sourceId, incidentColumns);
}

// A TechnicalStep field reference's sourceId is a technical step id (not an incident id), so the owning column is found by scanning the original/duplicate incidents' technical steps.
function getColumnIndexByTechnicalStepId(
  technicalStepId: number,
  incidentColumns: IncidentColumn[],
): number | undefined {
  for (let columnIndex = 1; columnIndex < incidentColumns.length; columnIndex++) {
    const hasMatchingTechnicalStep = incidentColumns[columnIndex].incident.technicalSteps?.some(
      (technicalStep) => technicalStep.id === technicalStepId,
    );

    if (hasMatchingTechnicalStep) {
      return columnIndex;
    }
  }

  return undefined;
}

function applySourcedSelectionToRimsSetItemRow(
  columns: RimsStepArrayItemColumn[] | undefined,
  columnIndex: number | undefined,
): void {
  if (columnIndex === undefined || !columns || !columns[columnIndex]) {
    return;
  }

  for (const column of columns) {
    column.isChecked = false;
  }

  columns[columnIndex].isChecked = true;
}

// Marks every nested field row inside a matched set item as checked for the same column, since the whole item was sourced from it.
function applySelectionToRimsSetItemChildren(rows: RimsStepRow[], columnIndex: number): void {
  for (const row of rows) {
    if (row.kind === 'field') {
      if (!row.row.columns[columnIndex]) {
        continue;
      }

      for (const column of row.row.columns) {
        column.isChecked = false;
      }

      row.row.columns[columnIndex].isChecked = true;
      continue;
    }

    applySelectionToRimsSetItemChildren(row.children, columnIndex);
  }
}

function getMergeViewColumns(
  mergedIncident: IncidentModel,
  originalIncident: IncidentModel,
  duplicateIncidents: IncidentModel[],
): IncidentColumn[] {
  return [
    { incident: mergedIncident, label: 'Merged' },
    { incident: originalIncident, label: 'Original' },
    ...duplicateIncidents.map((incident) => ({ incident, label: 'Duplicate' as const })),
  ];
}

function buildRimsStepRowsRecursive(
  stepId: string,
  properties: Record<string, RimsStepPropertySchema>,
  parentValues: (RimsStepValueContainer | null)[],
  incidentColumns: IncidentColumn[],
  isStepIncludedByIncident: boolean[],
  parentPath: string,
  depth: number,
  // True once we're nested inside a set/array item; such fields never show their own merge checkbox.
  isWithinArraySet: boolean,
): RimsStepRow[] {
  // We always render the whole rims step schema, even some fields are not filled in all incidents.
  // This way we are consistent and users can navigate the rims structure easily.
  return Object.entries(properties)
    .filter(([propertyName]) => !isHiddenRimsSetItemRootProperty(propertyName, parentPath))
    .map(([propertyName, propertySchema]) => {
      const propertyPath = parentPath ? `${parentPath}/${propertyName}` : propertyName;
      const propertyValues = parentValues.map((parentValue) => parentValue?.[propertyName] ?? null);
      const schemaTypes = Array.isArray(propertySchema.type) ? propertySchema.type : [propertySchema.type];

      // Set collection
      if (schemaTypes.includes('array') && propertySchema.items) {
        // arrayValues is an array of incident set items. If we have Merged (2 set items), Original (3 set items) and one Duplicate (1 set item) incident,
        // the colletion will consist of arrays like [ [2], [3], [1] ] <-- each array in the array contains set item information.
        const arrayValues = propertyValues.map((value) => (Array.isArray(value) ? value : []));

        const displayName = propertySchema.displayName ?? propertyName;

        // This is the set collection itself. The path begins with the step id.
        const fieldPath = `${stepId}/${propertyPath}`;

        // Set items sharing the same 'id' across incidents are aligned into one row; the rest get their own row with only their incident's column filled in.
        const itemValueGroups = groupRimsStepArrayItemsById(arrayValues);

        return {
          kind: 'array',
          displayName,
          field: `rims.${fieldPath}`,
          depth,
          children: itemValueGroups.map((itemValues, itemIndex) => {
            const itemFieldPath = `${fieldPath}/${itemIndex}`;

            return {
              kind: 'array-item',
              displayName: `${displayName} [${itemIndex + 1}]`,
              field: `rims.${itemFieldPath}`,
              depth: depth + 1,
              // The set collection's own field (without the item index), used by the merging logic to locate the array.
              arrayField: `rims.${fieldPath}`,
              // The shared 'id' of the set item this row represents, used to locate it within an incident's raw array.
              itemId: getSharedRimsStepItemId(itemValues),
              // Tracks which incident columns have a matching set item here, used to decide whether the set-level checkbox is shown.
              columns: incidentColumns.map((column, columnIndex) => ({
                incidentId: column.incident.id,
                hasValue: itemValues[columnIndex] !== null,
              })),
              children: buildRimsStepRowsRecursive(
                stepId,
                propertySchema.items?.properties ?? {},
                itemValues,
                incidentColumns,
                isStepIncludedByIncident,
                `${propertyPath}/${itemIndex}`,
                depth + 2,
                true,
              ),
            };
          }),
        };
      }

      // Object / folder
      if (schemaTypes.includes('object') && propertySchema.properties) {
        const childParentValues = propertyValues.map((value) =>
          typeof value === 'object' && value !== null && !Array.isArray(value) ? value : null,
        );

        return {
          kind: 'object',
          displayName: propertySchema.displayName ?? propertyName,
          field: `rims.${stepId}/${propertyPath}`,
          depth,
          children: buildRimsStepRowsRecursive(
            stepId,
            propertySchema.properties,
            childParentValues,
            incidentColumns,
            isStepIncludedByIncident,
            propertyPath,
            depth + 1,
            isWithinArraySet,
          ),
        };
      }

      // Text field (either text or date, boolean, enum presented as text)
      const row = createRow(
        incidentColumns,
        propertySchema.displayName ?? propertyName,
        'text',
        propertyValues.map((value, columnIndex) =>
          isStepIncludedByIncident[columnIndex]
            ? formatRimsStepPropertyValue(propertyName, value, propertySchema)
            : STEP_NOT_INCLUDED_LABEL,
        ),
        `rims.${stepId}/${propertyPath}`,
      );

      return {
        kind: 'field',
        isSetItemField: isWithinArraySet,
        row: {
          ...row,
          columns: row.columns.map((column, columnIndex) => ({
            ...column,
            ...(isStepIncludedByIncident[columnIndex] ? {} : { isStepNotIncluded: true }),
          })),
          depth,
        },
      };
    });
}

function isHiddenRimsSetItemRootProperty(propertyName: string, parentPath: string): boolean {
  const lastPathSegment = parentPath.split('/').at(-1);
  return propertyName === 'id' && lastPathSegment !== undefined && /^\d+$/.test(lastPathSegment);
}

// Groups per-incident set items by their 'id' field; matched ids align into one row, unmatched/missing-id items stay in their own column-only row.
function groupRimsStepArrayItemsById(arrayValues: RimsStepValueContainer[][]): (RimsStepValueContainer | null)[][] {
  // <set item id, <incident column index, set item value>>. This allows us to align set items sharing the same id across incidents into one row.
  const occurrencesById = new Map<string, Map<number, RimsStepValueContainer>>();

  for (let columnIndex = 0; columnIndex < arrayValues.length; columnIndex++) {
    for (const item of arrayValues[columnIndex]) {
      const itemId = getRimsStepItemId(item);
      if (itemId === null) {
        continue;
      }

      const occurrencesForId = occurrencesById.get(itemId) ?? new Map<number, RimsStepValueContainer>();
      if (!occurrencesForId.has(columnIndex)) {
        occurrencesForId.set(columnIndex, item);
      }
      occurrencesById.set(itemId, occurrencesForId);
    }
  }

  // Creating a new array of arrays where each inner array represents a row in the merge view. Each inner array contains the values for that row across all incident columns.
  const itemValueGroups: (RimsStepValueContainer | null)[][] = [];
  const processedIds = new Set<string>();

  for (let columnIndex = 0; columnIndex < arrayValues.length; columnIndex++) {
    for (const item of arrayValues[columnIndex]) {
      const itemId = getRimsStepItemId(item);

      if (itemId !== null) {
        if (processedIds.has(itemId)) {
          continue;
        }
        processedIds.add(itemId);

        const occurrencesForId = occurrencesById.get(itemId) ?? new Map<number, RimsStepValueContainer>();
        itemValueGroups.push(arrayValues.map((_, otherColumnIndex) => occurrencesForId.get(otherColumnIndex) ?? null));
        continue;
      }

      itemValueGroups.push(arrayValues.map((_, otherColumnIndex) => (otherColumnIndex === columnIndex ? item : null)));
    }
  }

  return itemValueGroups;
}

export function getRimsStepItemId(item: RimsStepValueContainer): string | null {
  const itemId = item['id'];
  return typeof itemId === 'string' && itemId.length > 0 ? itemId : null;
}

// The id shared by whichever incident columns have a value for this set item row (all populated columns share the same id, or there is only one).
function getSharedRimsStepItemId(itemValues: (RimsStepValueContainer | null)[]): string | null {
  for (const itemValue of itemValues) {
    if (itemValue) {
      return getRimsStepItemId(itemValue);
    }
  }

  return null;
}

export function formatRimsStepPropertyValue(
  propertyName: string,
  value: RimsStepProperty,
  propertySchema: RimsStepPropertySchema,
): MergeColumnValueType {
  if (value === null || value === undefined) {
    return null;
  }

  if (propertySchema.enum && typeof value === 'string') {
    return propertySchema.enum.find((enumValue) => enumValue.value === value)?.displayValue ?? value;
  }

  if (propertySchema.format === 'date' && typeof value === 'string') {
    return formatAsDateOnlyString(value);
  }

  // If for some reason the value in a field is an object or array, we cannot display it in the merge view.
  // Log in the console so it's not completely silent, but return null to avoid breaking the merge view.
  if (Array.isArray(value) || typeof value === 'object') {
    console.warn(`Failed to format RIMS property "${propertyName}".`);
    return null;
  }

  return typeof value === 'boolean' ? value : String(value);
}

function createDetailsRows(incidentColumns: IncidentColumn[]): MergeRow[] {
  const reviewedRow = createRow(
    incidentColumns,
    '',
    'slide-toggle',
    incidentColumns.map((column) => column.incident.isReviewed),
    'isReviewed',
  );
  reviewedRow.hasConflict = false;

  return [reviewedRow, ...createRowsFromDefinitions(incidentColumns, detailsRowDefinitions)];
}

function createEntityRows(incidentColumns: IncidentColumn[]): MergeRow[] {
  return createRowsFromDefinitions(incidentColumns, entityRowDefinitions);
}

function createThreatActorRows(incidentColumns: IncidentColumn[]): MergeRow[] {
  return createRowsFromDefinitions(incidentColumns, threatActorRowDefinitions);
}

function createArticleReferenceRows(incidentColumns: IncidentColumn[]): ReferenceRow[] {
  return createGroupableReferenceRows(
    incidentColumns,
    'Article',
    'article',
    'custom.articleReference',
    (incident) => incident.articleReferences,
    getGroupableArticleReferenceId,
  );
}

function createMediaReferenceRows(incidentColumns: IncidentColumn[]): ReferenceRow[] {
  return createGroupableReferenceRows(
    incidentColumns,
    'File',
    'media',
    'custom.mediaReference',
    (incident) => incident.mediaReferences,
    getGroupableMediaReferenceId,
  );
}

// Persisted media references (not selected for upload in the edit mode) can be grouped by id; "create" ones (with createId) cannot.
function getGroupableMediaReferenceId(item: MergeColumnValueType): number | null {
  if (typeof item !== 'object' || item === null || !('id' in item) || 'createId' in item) {
    return null;
  }

  return (item as MediaReferenceMetadataModel).id;
}

// Article references created in the current edit session have no id yet, so they can never be grouped.
function getGroupableArticleReferenceId(item: MergeColumnValueType): number | null {
  if (typeof item !== 'object' || item === null || !('url' in item) || !('id' in item)) {
    return null;
  }

  return (item as ArticleReferenceModel).id;
}

// Groups reference items (article/media) sharing the same id across incident columns into one row; the rest stay single-column.
function createGroupableReferenceRows(
  incidentColumns: IncidentColumn[],
  displayName: string,
  type: 'article' | 'media',
  field: IncidentModelFieldName,
  itemsSelector: (incident: IncidentModel) => MergeColumnValueType[] | null | undefined,
  getGroupableId: (item: MergeColumnValueType) => number | null,
): ReferenceRow[] {
  const occurrencesById = new Map<number, { columnIndex: number; item: MergeColumnValueType }[]>();

  for (let columnIndex = 0; columnIndex < incidentColumns.length; columnIndex++) {
    const items = itemsSelector(incidentColumns[columnIndex].incident) ?? [];

    for (const item of items) {
      const groupableId = getGroupableId(item);
      if (groupableId === null) {
        continue;
      }

      const occurrences = occurrencesById.get(groupableId) ?? [];
      occurrences.push({ columnIndex, item });
      occurrencesById.set(groupableId, occurrences);
    }
  }

  const rows: ReferenceRow[] = [];
  const processedIds = new Set<number>();

  for (let columnIndex = 0; columnIndex < incidentColumns.length; columnIndex++) {
    const items = itemsSelector(incidentColumns[columnIndex].incident) ?? [];

    for (const item of items) {
      const groupableId = getGroupableId(item);

      if (groupableId !== null) {
        if (processedIds.has(groupableId)) {
          continue;
        }
        processedIds.add(groupableId);

        const occurrences = occurrencesById.get(groupableId) ?? [];
        if (occurrences.length > 1) {
          rows.push(createGroupedReferenceRow(incidentColumns, displayName, type, occurrences));
          continue;
        }
      }

      rows.push(createSingleColumnReferenceRow(incidentColumns, displayName, type, field, columnIndex, item));
    }
  }

  return rows;
}

function createGroupedReferenceRow(
  incidentColumns: IncidentColumn[],
  displayName: string,
  type: 'article' | 'media',
  occurrences: { columnIndex: number; item: MergeColumnValueType }[],
): MergeRow {
  const valueByColumnIndex = new Map(occurrences.map(({ columnIndex, item }) => [columnIndex, item]));

  return {
    displayName,
    field: undefined,
    type,
    hasConflict: false,
    columns: incidentColumns.map((column, columnIndex) => ({
      incidentId: column.incident.id,
      value: valueByColumnIndex.get(columnIndex),
    })),
  };
}

function createSingleColumnReferenceRow(
  incidentColumns: IncidentColumn[],
  displayName: string,
  type: 'article' | 'media',
  field: IncidentModelFieldName,
  columnIndex: number,
  item: MergeColumnValueType,
): SingleColumnMergeRow {
  return {
    displayName,
    field,
    type,
    hasConflict: false,
    columnIndex,
    column: {
      incidentId: incidentColumns[columnIndex].incident.id,
      value: item,
    },
  };
}

function createRowsFromDefinitions(incidentColumns: IncidentColumn[], rowDefinitions: RowDefinition[]): MergeRow[] {
  return rowDefinitions.map((rowDefinition) =>
    createRow(
      incidentColumns,
      rowDefinition.displayName,
      rowDefinition.type,
      incidentColumns.map((column) => rowDefinition.valueSelector(column.incident)),
      rowDefinition.field,
      rowDefinition.description,
    ),
  );
}

function createRow(
  incidentColumns: IncidentColumn[],
  displayName: string,
  type: RowType,
  values: (MergeColumnValueType | undefined)[],
  field?: IncidentModelFieldName,
  description?: string | null,
): MergeRow {
  const columns: MergeColumn[] = values.map((value, index) => ({
    incidentId: incidentColumns[index]?.incident.id,
    value,
  }));

  return {
    displayName,
    field,
    description: description ?? undefined,
    type,
    hasConflict: hasConflict(columns),
    columns,
  };
}

function hasConflict(columns: MergeColumn[]): boolean {
  if (columns.length <= 1) {
    return false;
  }

  const normalizeValueForComparison = (value: MergeColumnValueType | undefined): string => {
    if (value === undefined || value === null) {
      return '';
    }

    if (typeof value === 'object') {
      if ('countryCode' in value) {
        return String(value.countryCode ?? 'Unknown');
      }

      if ('id' in value && 'contentType' in value) {
        return `${value.id}|${value.contentType}`;
      }
    }

    return String(value).trim();
  };

  const mergedValue = normalizeValueForComparison(columns[0]?.value);
  return columns.slice(1).some((column) => normalizeValueForComparison(column.value) !== mergedValue);
}
